import { formatCompactSignedMoney, TenantCurrency } from 'src/common/currency';
import { horizonAge } from '../kpis';
import { ScenarioKpis, ScenarioSummary } from '../types';
import {
  ageAfter,
  agesAfter,
  deathsWithin,
  endingDeath,
  ModelTimeline,
  modelTimeline,
  yearAfter,
} from './timeline';

export type OutcomeStatus = 'lasts' | 'shortLate' | 'shortEarly' | 'notProjected';

export type ModelOutcome = {
  status: OutcomeStatus;
  verdict: string;
  details: string[];
};

type JudgedStatus = Exclude<OutcomeStatus, 'notProjected'>;

const NO_SHORTFALL = 'No shortfall before the end of the plan';

const yearsFrom = (years: number, relation: 'before' | 'after', point: string): string =>
  years === 0 ? `Around ${point}` : `${years} year${years === 1 ? '' : 's'} ${relation} ${point}`;

// A household's life expectancy is its second death; once one partner's death is recorded,
// it is the survivor's alone.
const lifeExpectancyName = (timeline: ModelTimeline): string => {
  const unrecorded = timeline.people.filter(person => person.diesAfter == null);
  if (timeline.people.length === 1) return 'life expectancy';
  if (unrecorded.length === 1) return `${unrecorded[0].name}'s life expectancy`;
  return 'joint life expectancy';
};

type Benchmark = { after: number; name: string };

/**
 * What a shortfall is measured against: the recorded death a plan ends at, since everyone in
 * it is alive until then, or otherwise the household's life expectancy.
 */
const benchmarkOf = (timeline: ModelTimeline): Benchmark | null => {
  const ending = endingDeath(timeline);
  if (ending) return { after: ending.after, name: `${ending.name}'s recorded death` };
  return timeline.lifeExpectancyAfter == null
    ? null
    : { after: timeline.lifeExpectancyAfter, name: lifeExpectancyName(timeline) };
};

const measuredAgainst = (after: number, benchmark: Benchmark): string =>
  yearsFrom(
    Math.round(Math.abs(benchmark.after - after)),
    after < benchmark.after ? 'before' : 'after',
    benchmark.name,
  );

/** The whole years a plan stops short of life expectancy, when it ends before it. */
const yearsShortOfLifeExpectancy = (timeline: ModelTimeline): number | null => {
  if (endingDeath(timeline) != null || timeline.lifeExpectancyAfter == null) return null;
  const years = Math.round(timeline.lifeExpectancyAfter - timeline.length);
  return years > 0 ? years : null;
};

/**
 * Money that lasts to the end of a plan lasts for life, unless the plan stops before life
 * expectancy: then all it shows is that the money lasts to the plan's end.
 */
const lastingOutcome = (planEnd: string, timeline: ModelTimeline | null): ModelOutcome => {
  const yearsShort = timeline ? yearsShortOfLifeExpectancy(timeline) : null;
  if (timeline == null || yearsShort == null) {
    return { status: 'lasts', verdict: 'Lasts for life', details: [NO_SHORTFALL] };
  }
  return {
    status: 'lasts',
    verdict: `Lasts to ${planEnd}`,
    details: [`The plan ends ${yearsFrom(yearsShort, 'before', lifeExpectancyName(timeline))}`],
  };
};

/**
 * A household's outcome reads in calendar years, with each partner's age, because no one
 * person's age places it. A plan that ends at a recorded death is read against that death.
 */
const householdOutcome = (status: JudgedStatus, timeline: ModelTimeline): ModelOutcome => {
  const ending = endingDeath(timeline);
  const { shortfallAfter } = timeline;
  if (shortfallAfter == null) {
    return ending
      ? {
          status,
          verdict: 'Lasts for life',
          details: [
            `Until ${ending.name}'s recorded death`,
            `The plan ends in ${yearAfter(timeline, timeline.length)}`,
          ],
        }
      : lastingOutcome(String(yearAfter(timeline, timeline.length)), timeline);
  }
  const verdict = `Runs short in ${yearAfter(timeline, shortfallAfter)}`;
  const ages = agesAfter(timeline, shortfallAfter);
  const benchmark = benchmarkOf(timeline);
  return {
    status,
    verdict,
    details: benchmark ? [ages, measuredAgainst(shortfallAfter, benchmark)] : [ages],
  };
};

const statusOf = (kpis: ScenarioKpis, timeline: ModelTimeline | null): JudgedStatus => {
  const shortfall = kpis.firstShortfallAge;
  if (shortfall == null) return 'lasts';
  if (timeline != null && endingDeath(timeline) != null) return 'shortEarly';
  const lifeExpectancy = kpis.lifeExpectancyAge;
  return lifeExpectancy == null || shortfall < lifeExpectancy ? 'shortEarly' : 'shortLate';
};

/**
 * Whether a model's money lasts. Green never runs short before the end of the plan,
 * amber runs short only after the household's life expectancy, and red runs short
 * within it, or at all in a plan that ends at a recorded death. Projections made before
 * the engine reported life expectancy cannot tell amber from red, so their shortfall
 * reads as red until the model is run again.
 */
export const modelOutcome = (kpis: ScenarioKpis | null): ModelOutcome => {
  if (kpis == null) {
    return {
      status: 'notProjected',
      verdict: 'Not projected yet',
      details: ['Open the model to run it'],
    };
  }
  const timeline = modelTimeline(kpis);
  const status = statusOf(kpis, timeline);
  if (timeline && timeline.people.length > 1) {
    return householdOutcome(status, timeline);
  }
  const shortfall = kpis.firstShortfallAge;
  if (shortfall == null) {
    return lastingOutcome(String(horizonAge(kpis.horizonAge)), timeline);
  }
  const verdict = `Runs short at ${Math.floor(shortfall)}`;
  const benchmark = timeline ? benchmarkOf(timeline) : null;
  if (timeline?.shortfallAfter == null || benchmark == null) {
    return { status, verdict, details: [] };
  }
  return { status, verdict, details: [measuredAgainst(timeline.shortfallAfter, benchmark)] };
};

/**
 * Where a model's plan ends: one person's plan at an age, a household's in a year, and
 * either at a recorded death once everyone in it has one.
 */
const planEnd = (kpis: ScenarioKpis) => {
  const timeline = modelTimeline(kpis);
  const household = timeline != null && timeline.people.length > 1;
  return {
    household,
    at: household
      ? `in ${yearAfter(timeline, timeline.length)}`
      : `at ${horizonAge(kpis.horizonAge)}`,
    death: timeline ? endingDeath(timeline) : null,
  };
};

/** Where a model's legacy is measured, which varies with the firm's cut-off and the model's own. */
export const legacyLabel = (kpis: ScenarioKpis): string => {
  const { at, death } = planEnd(kpis);
  return `${death ? 'Estate' : 'Legacy'} ${at}`;
};

/**
 * How a model's legacy compares with its primary model's, e.g. "£1.65m below the primary".
 * A model whose plan ends at a different point is not measured against it.
 */
export const comparedWithPrimary = (
  model: ScenarioKpis | null,
  primary: ScenarioKpis | null,
  currency: TenantCurrency,
): string | null => {
  if (model?.legacyReal == null || primary?.legacyReal == null) {
    return null;
  }
  if (model.horizonDate !== primary.horizonDate) {
    const { at, death } = planEnd(model);
    return `Ends ${death ? `at ${death.name}'s death` : at}, so not compared`;
  }
  const difference = model.legacyReal - primary.legacyReal;
  const amount = formatCompactSignedMoney(Math.abs(difference), currency);
  if (amount === formatCompactSignedMoney(0, currency)) {
    return 'Same as the primary';
  }
  return `${amount} ${difference > 0 ? 'above' : 'below'} the primary`;
};

export const legacyFigure = (scenario: ScenarioSummary, currency: TenantCurrency): string =>
  scenario.kpis?.legacyReal != null
    ? formatCompactSignedMoney(scenario.kpis.legacyReal, currency)
    : '—';

/** What the bar under a model's outcome says to a screen reader. */
export const timelineLabel = (timeline: ModelTimeline): string => {
  const { people, length, shortfallAfter, lifeExpectancyAfter } = timeline;
  const household = people.length > 1;
  const value = (after: number) =>
    household ? yearAfter(timeline, after) : ageAfter(people[0], after);
  const point = (after: number) => `${household ? 'in' : 'at'} ${value(after)}`;
  const span = household
    ? `from ${yearAfter(timeline, 0)} to ${yearAfter(timeline, length)}`
    : // The plan ends in the month its youngest member reaches the cut-off, so just short of it.
      `from age ${ageAfter(people[0], 0)} to ${Math.round(people[0].ageAtStart + length)}`;
  const parts = [
    shortfallAfter == null
      ? 'Money lasts to the end of the plan'
      : `Money runs short ${point(shortfallAfter)}`,
    `on a plan ${span}`,
    ...(lifeExpectancyAfter != null && endingDeath(timeline) == null
      ? [`${lifeExpectancyName(timeline)} ${value(lifeExpectancyAfter)}`]
      : []),
    ...deathsWithin(timeline).map(death => `${death.name} dies ${point(death.after)}`),
  ];
  return parts.join(', ');
};

export type PlanScale = {
  start: string[];
  lifeExpectancy: { lines: string[]; after: number } | null;
  end: string[];
};

const capitalise = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/** What the tick on a model's bar marks: "Joint life expectancy · 2059", "Life expectancy · 86". */
export const lifeExpectancyLabel = (timeline: ModelTimeline): string | null => {
  const { people, lifeExpectancyAfter } = timeline;
  if (lifeExpectancyAfter == null || endingDeath(timeline) != null) {
    return null;
  }
  const at =
    people.length === 1
      ? ageAfter(people[0], lifeExpectancyAfter)
      : yearAfter(timeline, lifeExpectancyAfter);
  return `${capitalise(lifeExpectancyName(timeline))} · ${at}`;
};

/**
 * The labels under a primary model's bar: today, the household's life expectancy and the
 * end of the plan, each with the ages that place it.
 */
export const planScale = (kpis: ScenarioKpis, timeline: ModelTimeline): PlanScale => {
  const { people, length, lifeExpectancyAfter } = timeline;
  const ending = endingDeath(timeline);
  const lifeExpectancy =
    ending == null && lifeExpectancyAfter != null && lifeExpectancyAfter <= length
      ? lifeExpectancyAfter
      : null;
  if (people.length === 1) {
    const [person] = people;
    return {
      start: [`Today · ${ageAfter(person, 0)}`],
      lifeExpectancy:
        lifeExpectancy == null
          ? null
          : {
              lines: [`Life expectancy · ${ageAfter(person, lifeExpectancy)}`],
              after: lifeExpectancy,
            },
      end: [`End of plan · ${horizonAge(kpis.horizonAge)}`],
    };
  }
  // The plan runs until the youngest still alive reaches the cut-off age.
  const youngest = people
    .filter(person => person.diesAfter == null)
    .sort((a, b) => a.ageAtStart - b.ageAtStart)[0];
  return {
    start: [`Today · ${yearAfter(timeline, 0)}`, agesAfter(timeline, 0)],
    lifeExpectancy:
      lifeExpectancy == null
        ? null
        : {
            lines: [
              `${capitalise(lifeExpectancyName(timeline))} · ${yearAfter(timeline, lifeExpectancy)}`,
              agesAfter(timeline, lifeExpectancy),
            ],
            after: lifeExpectancy,
          },
    end: [
      `End of plan · ${yearAfter(timeline, length)}`,
      ending
        ? `${ending.name}'s recorded death`
        : `${youngest.name} ${horizonAge(kpis.horizonAge)}`,
    ],
  };
};
